import type { IconProps } from '@carboncopyco/icons';
import {
  TextTooltip,
  Tooltip,
  TooltipMessage,
  type TooltipProps,
} from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import classNames from 'classnames';
import type React from 'react';
import { type MouseEventHandler, useContext, useRef } from 'react';
import { DescriptionLink } from '../DescriptionLink';
import { FormBuilderContext } from './FormBuilderContext';

const helpLink =
  'https://www.notion.so/demostack/Data-Rules-How-To-8798490f2d4a4b47a83df58750674213';

export interface ActionIconProps {
  iconComponent: React.FC<IconProps>;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

export const ActionIcon = ({
  iconComponent: Icon,
  label,
  onClick,
  disabled,
}: ActionIconProps) => {
  const { onLearnMoreClick } = useContext(FormBuilderContext);
  const anchorRef = useRef(null);
  const commonTooltipProps = {
    anchorRef,
    offset: [0, 2],
    delay: [700, 0],
    shadow: 'lg',
  } satisfies Partial<TooltipProps>;

  const handleClick: MouseEventHandler<HTMLSpanElement> = (e) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <>
      <span
        className={classNames(
          disabled
            ? 'text-ds-natural-200'
            : 'text-ds-primary-300 cursor-pointer'
        )}
        ref={anchorRef}
        onClick={disabled ? undefined : handleClick}
      >
        <Icon size={16} />
      </span>
      {!disabled && (
        <Tooltip
          {...commonTooltipProps}
          noArrow
          zIndex={getMaxZIndex() + 1}
          placement="top"
          interactiveBorder={0}
          background="dark"
        >
          <span className="p-1">
            <TextTooltip
              color="text-ds-natural-50"
              label={label}
            />
          </span>
        </Tooltip>
      )}
      {disabled && (
        <Tooltip
          {...commonTooltipProps}
          noArrow
          zIndex={getMaxZIndex() + 10}
          placement="bottom"
          interactive
        >
          <TooltipMessage
            title={
              <DescriptionLink
                label={`This property can't be added to form. You can display the data as raw JSON.`}
                linkLabel="Learn more"
                linkUrl={helpLink}
                onLearnMoreClick={onLearnMoreClick}
              />
            }
          />
        </Tooltip>
      )}
    </>
  );
};
